import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, catchError, map, of, tap, filter } from "rxjs";
import { PaymentsMetricsService } from "@apps/patient-app/src/app/services/analytics/payments-metrics.service";
import { DepositPaymentsMetricsService } from "@apps/patient-app/src/app/services/analytics/deposit-payments-metrics.service";
import { HttpService } from "@apps/patient-core/src/app/services/http.service";
import { LocationService } from "@apps/patient-core/src/app/services/location.service";

export enum E_StripePaymentMethod {
  card = "card",
  applePay = "apple_pay",
  googlePay = "google_pay",
}

export enum E_PaymentState {
  LOADING = "LOADING",
  READY = "READY",
  ERROR = "ERROR",
  TAKING_PAYMENT = "TAKING_PAYMENT",
  SUCCESS = "SUCCESS",
}

export enum PaymentType {
  BALANCE = "balance",
  DEPOSIT = "deposit",
}
interface I_PaymentState {
  state: E_PaymentState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  paymentMethod?: string;
}

type AppointmentBookingProps = {
  site_appointment_types: {
    id: string;
    practitioner_id: string;
    site_id: string;
  }[];
  appointment_ids: string[];
  is_new_patient: boolean;
};
type BalancePaymentProps = {
  amount: number;
};
type DepositLinkProps = {
  short_code: string;
};
type StripeCheckoutSessionProps = AppointmentBookingProps | BalancePaymentProps | DepositLinkProps;
@Injectable({
  providedIn: "root",
})
export class PaymentsService {
  public formComplete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public paymentState = new BehaviorSubject<I_PaymentState | null>(null);

  constructor(
    private _http: HttpService,
    private _locationService: LocationService,
    private _paymentsMetricsService: PaymentsMetricsService,
    private _depositPaymentsMetricsService: DepositPaymentsMetricsService
  ) {}

  public trackPaymentDone(siteId: string, paymentType: PaymentType): void {
    switch (paymentType) {
      case PaymentType.BALANCE:
        this._paymentsMetricsService.trackPaymentDone(siteId);
        break;
      case PaymentType.DEPOSIT:
        this._depositPaymentsMetricsService.trackDepositPaymentDone(siteId);
        break;
      default:
        throw new Error(`Invalid payment type: ${paymentType}`);
    }
  }

  public createStripeCheckoutSession(props: StripeCheckoutSessionProps): Observable<string> {
    const newItem = this._getNewItem(props, true);
    return this._http
      .mutation(
        "createStripeCheckoutSession",
        `{
      createStripeCheckoutSession(new_item: {
        ${newItem}
      }) {
        url
      }
    }`
      )
      .pipe(
        catchError(() => {
          this._handlePaymentError();

          return of(null);
        }),
        filter((result) => !!result),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((result: any) => {
          const data = result.data?.createStripeCheckoutSession;

          if (!data) {
            this._handlePaymentError();
            return;
          }

          return data.url;
        }),
        tap((url) => {
          window.open(url, "_self");
        })
      );
  }

  private _getNewItem(props: StripeCheckoutSessionProps, includeOrigin = false): string {
    let newItem = `hostname: "${this._locationService.hostname}"`;

    if (includeOrigin) {
      newItem = `${newItem},
        origin: "${this._locationService.actualHostname}"`;
    }

    if ("amount" in props) {
      newItem = `${newItem}
        ${this._getBalancePaymentNewItem(props)}`;
    } else if ("site_appointment_types" in props) {
      newItem = `${newItem}
        ${this._getAppointmentBookingNewItem(props)}`;
    } else if ("short_code" in props) {
      newItem = `${newItem}
        ${this._getDepositLinkNewItem(props)}`;
    }

    return newItem;
  }

  private _getAppointmentBookingNewItem(props: AppointmentBookingProps): string {
    return `
      site_appointment_types: [${props.site_appointment_types
        .map(
          (siteAppointmentType) => `{
        id: "${siteAppointmentType.id}",
        practitioner_id: "${siteAppointmentType.practitioner_id}"
        site_id: "${siteAppointmentType.site_id}"
      }`
        )
        .join(", ")}]
      appointment_ids: [${props.appointment_ids.map((id) => `"${id}"`).join(", ")}]
      isNewPatient: ${props.is_new_patient}
    `;
  }

  private _getBalancePaymentNewItem(props: BalancePaymentProps): string {
    return `amount: ${props.amount}`;
  }

  private _getDepositLinkNewItem(props: DepositLinkProps): string {
    return `short_code: "${props.short_code}"`;
  }

  private _handlePaymentError(): void {
    this.paymentState.next({
      state: E_PaymentState.ERROR,
      data: {
        message: "Something went wrong. Please try again later.",
      },
    });
  }
}
